/** Settings → Billing: VAT, due days, tolerated cash gap, mentions and instructions; exports for the accountant. */

import {
  type BillingExportsRetrieveDataset,
  type BillingExportsRetrieveFileFormat,
  type BillingSettings,
  getBillingExportsRetrieveUrl,
  refreshSession,
  useBillingSettingsList,
  useBillingSettingsPartialUpdate,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Button, GlassCard, SelectField, Skeleton, SoftToggle, TextField } from "@agencyos/ui-web";
import { Download } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { asTexts, type Texts, TranslatedFields } from "../components/TranslatedFields";
import i18next from "../lib/i18n";
import { session } from "../lib/session";
import { fieldErrors, useCanEditSettings } from "../lib/settings";
import { amountOf, todayInDouala, useBillingRoles, useRefreshBilling } from "./billing/shared";

/** « 19,25 » or « 19.25 » → 1925 basis points (NaN when not a number). */
function toBasisPoints(text: string): number {
  const value = Number.parseFloat(text.replace(",", ".").replace(/[^\d.]/g, ""));
  return Number.isNaN(value) ? Number.NaN : Math.round(value * 100);
}

function fromBasisPoints(bp: number, language: string): string {
  return new Intl.NumberFormat(language === "en" ? "en-GB" : language, { maximumFractionDigits: 2, useGrouping: false }).format(bp / 100);
}

function SettingsForm({ row }: { row: BillingSettings }) {
  const { t, i18n } = useTranslation();
  const canEdit = useCanEditSettings();
  const save = useBillingSettingsPartialUpdate({ mutation: { onSuccess: useRefreshBilling() } });
  const [vatEnabled, setVatEnabled] = useState(Boolean(row.vat_enabled));
  const [rate, setRate] = useState(fromBasisPoints(row.vat_rate_bp ?? 0, i18n.language));
  const [included, setIncluded] = useState(Boolean(row.prices_include_vat));
  const [dueDays, setDueDays] = useState(String(row.invoice_due_days ?? 0));
  const [threshold, setThreshold] = useState(String(row.cash_alert_threshold_xaf ?? 0));
  const [mentions, setMentions] = useState<Texts>(asTexts(row.legal_mentions));
  const [instructions, setInstructions] = useState<Texts>(asTexts(row.payment_instructions));
  const [rateError, setRateError] = useState("");
  const errors = fieldErrors(save.error);

  const onSave = () => {
    const bp = toBasisPoints(rate);
    if (vatEnabled && (Number.isNaN(bp) || bp < 0)) return setRateError(t("billing.settings.rateInvalid"));
    setRateError("");
    const days = amountOf(dueDays);
    const gap = amountOf(threshold);
    save.mutate({
      id: row.id,
      data: {
        vat_enabled: vatEnabled,
        ...(Number.isNaN(bp) ? {} : { vat_rate_bp: bp }),
        prices_include_vat: included,
        invoice_due_days: Number.isNaN(days) ? 0 : days,
        cash_alert_threshold_xaf: Number.isNaN(gap) ? 0 : gap,
        legal_mentions: mentions,
        payment_instructions: instructions,
      },
    });
  };

  return (
    <GlassCard as="section" className="stack">
      <fieldset className="plain-fieldset stack" disabled={!canEdit}>
        <div className="form-section">
          <div className="form-section__head">
            <h2>{t("billing.settings.vat")}</h2>
            <p className="muted">{t("billing.settings.vatIntro")}</p>
          </div>
          <div className="stack">
            <SoftToggle label={t("billing.settings.vatEnabled")} checked={vatEnabled} onChange={setVatEnabled} disabled={!canEdit} />
            <div className="form-grid">
              <TextField
                label={t("billing.settings.rate")}
                hint={t("billing.settings.rateHint")}
                inputMode="decimal"
                value={rate}
                disabled={!vatEnabled || !canEdit}
                error={rateError || errors.vat_rate_bp}
                onChange={(e) => setRate(e.target.value)}
              />
            </div>
            <SoftToggle label={t("billing.settings.included")} checked={included} onChange={setIncluded} disabled={!vatEnabled || !canEdit} />
            <p className="muted">{included ? t("billing.settings.includedHint") : t("billing.settings.excludedHint")}</p>
          </div>
        </div>
        <div className="form-section">
          <div className="form-section__head">
            <h2>{t("billing.settings.rules")}</h2>
            <p className="muted">{t("billing.settings.rulesIntro")}</p>
          </div>
          <div className="form-grid">
            <TextField
              label={t("billing.settings.dueDays")}
              hint={t("billing.settings.dueDaysHint")}
              inputMode="numeric"
              value={dueDays}
              error={errors.invoice_due_days}
              onChange={(e) => setDueDays(e.target.value.replace(/\D/g, ""))}
            />
            <TextField
              label={t("billing.settings.threshold")}
              hint={t("billing.settings.thresholdHint")}
              inputMode="numeric"
              value={threshold ? formatXaf(amountOf(threshold), { suffix: false }) : ""}
              error={errors.cash_alert_threshold_xaf}
              onChange={(e) => setThreshold(e.target.value.replace(/\D/g, ""))}
            />
          </div>
        </div>
        <div className="form-section">
          <div className="form-section__head">
            <h2>{t("billing.settings.texts")}</h2>
            <p className="muted">{t("billing.settings.textsIntro")}</p>
          </div>
          <div className="stack">
            <TranslatedFields
              label={t("billing.settings.mentions")}
              value={mentions}
              onChange={setMentions}
              multiline
              required={false}
              disabled={!canEdit}
              error={errors.legal_mentions}
            />
            <TranslatedFields
              label={t("billing.settings.instructions")}
              value={instructions}
              onChange={setInstructions}
              multiline
              required={false}
              disabled={!canEdit}
              error={errors.payment_instructions}
            />
          </div>
        </div>
        {canEdit ? (
          <div className="row">
            <Button disabled={save.isPending} onClick={onSave}>
              {save.isPending ? t("common.loading") : t("common.save")}
            </Button>
            {save.isSuccess ? (
              <span role="status" className="ok">
                {t("common.saved")}
              </span>
            ) : null}
            {save.isError && !Object.keys(errors).length ? (
              <span role="alert" className="ko">
                {t("common.error")}
              </span>
            ) : null}
          </div>
        ) : null}
      </fieldset>
    </GlassCard>
  );
}

/**
 * The export is a binary file: the generated client parses JSON, so it is fetched here with the
 * same credentials (token, agency, language) and saved through a Blob URL.
 */
async function downloadExport(params: {
  dataset: BillingExportsRetrieveDataset;
  file_format: BillingExportsRetrieveFileFormat;
  start?: string;
  end?: string;
}): Promise<void> {
  const url = `${import.meta.env.VITE_API_URL ?? ""}${getBillingExportsRetrieveUrl(params)}`;
  const send = () => {
    const headers = new Headers({ "Accept-Language": i18next.language, "X-Client": "web" });
    const { accessToken, organization } = session.get();
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    if (organization) headers.set("X-Organization", organization);
    return fetch(url, { credentials: "include", headers });
  };
  let response = await send();
  if (response.status === 401 && (await refreshSession())) response = await send();
  if (!response.ok) throw new Error(`Export failed: ${response.status}`);
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `${params.dataset}.${params.file_format}`;
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}

function Exports() {
  const { t } = useTranslation();
  const today = todayInDouala();
  const [dataset, setDataset] = useState<BillingExportsRetrieveDataset>("invoices");
  const [format, setFormat] = useState<BillingExportsRetrieveFileFormat>("xlsx");
  const [start, setStart] = useState(`${today.slice(0, 7)}-01`);
  const [end, setEnd] = useState(today);
  const [state, setState] = useState<"idle" | "busy" | "error">("idle");
  const invalid = Boolean(start && end && start > end);
  const onDownload = () => {
    setState("busy");
    downloadExport({ dataset, file_format: format, ...(start ? { start } : {}), ...(end ? { end } : {}) })
      .then(() => setState("idle"))
      .catch(() => setState("error"));
  };
  return (
    <GlassCard as="section" className="stack">
      <div>
        <h2>{t("billing.exports.title")}</h2>
        <p className="muted">{t("billing.exports.intro")}</p>
      </div>
      <div className="form-grid">
        <SelectField
          label={t("billing.exports.dataset")}
          value={dataset}
          options={(["invoices", "payments"] as const).map((d) => ({ value: d, label: t(`billing.exports.datasets.${d}`) }))}
          onChange={(e) => setDataset(e.target.value as BillingExportsRetrieveDataset)}
        />
        <SelectField
          label={t("billing.exports.format")}
          value={format}
          options={(["xlsx", "csv"] as const).map((f) => ({ value: f, label: t(`billing.exports.formats.${f}`) }))}
          onChange={(e) => setFormat(e.target.value as BillingExportsRetrieveFileFormat)}
        />
        <TextField label={t("billing.exports.start")} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        <TextField
          label={t("billing.exports.end")}
          type="date"
          value={end}
          error={invalid ? t("billing.exports.invalidRange") : undefined}
          onChange={(e) => setEnd(e.target.value)}
        />
      </div>
      <div className="row">
        <Button icon={<Download size={16} aria-hidden="true" />} disabled={state === "busy" || invalid} onClick={onDownload}>
          {state === "busy" ? t("common.loading") : t("billing.exports.download")}
        </Button>
        {state === "error" ? (
          <span role="alert" className="ko">
            {t("billing.exports.failed")}
          </span>
        ) : null}
      </div>
    </GlassCard>
  );
}

export function BillingSettingsPage() {
  const { t } = useTranslation();
  const { canExport } = useBillingRoles();
  const settings = useBillingSettingsList();
  const row = settings.data?.status === 200 ? settings.data.data.results[0] : undefined;
  return (
    <SettingsLayout title={t("billing.settings.title")} intro={t("billing.settings.intro")}>
      <div className="stack">
        {row ? <SettingsForm key={row.id} row={row} /> : settings.isError ? <p role="alert" className="ko">{t("common.error")}</p> : <Skeleton style={{ height: 240 }} />}
        {canExport ? <Exports /> : null}
      </div>
    </SettingsLayout>
  );
}
